'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, DropdownProps } from 'react-day-picker';
import { addDays, subDays, addWeeks, subWeeks, isSameDay } from 'date-fns';

import { cn } from '@samera-one/helpers';
import { buttonVariants } from '~atoms/button/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~atoms/select/select';
import { ScrollArea } from '~atoms/scroll-area/scroll-area';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const currentFocusedDateRef = React.useRef<Date | null>(null);
  const pendingFocusRef = React.useRef<number | null>(null);

  /**
   * Track currently focused date
   */
  const handleDayFocus = React.useCallback((date: Date) => {
    currentFocusedDateRef.current = date;
  }, []);

  /**
   * Simple check if a date is disabled based on props.disabled
   */
  const isDateDisabled = React.useCallback(
    (date: Date): boolean => {
      if (!props.disabled) return false;
      if (typeof props.disabled === 'boolean') return props.disabled;
      if (typeof props.disabled === 'function') return props.disabled(date);

      // Array of matchers
      if (Array.isArray(props.disabled)) {
        return props.disabled.some(matcher => {
          // Date matcher
          if (matcher instanceof Date) {
            return isSameDay(date, matcher);
          }
          // Function matcher
          if (typeof matcher === 'function') {
            return matcher(date);
          }
          // Range matcher: { before?: Date, after?: Date }
          if (matcher && typeof matcher === 'object') {
            const range = matcher as any;
            if (range.before && date < range.before) return true;
            if (range.after && date > range.after) return true;
          }
          return false;
        });
      }

      // Single object matcher
      if (typeof props.disabled === 'object') {
        const range = props.disabled as any;
        if (range.before && date < range.before) return true;
        if (range.after && date > range.after) return true;
      }

      return false;
    },
    [props.disabled],
  );

  /**
   * Custom keyboard navigation for calendar
   *
   * Intercepts arrow key navigation to:
   * - Block navigation to disabled dates
   * - Block navigation outside the visible month range
   * - Skip over disabled dates to find the next enabled date
   * - Handle multiple buttons with the same day number (e.g., Oct 28 and Nov 28)
   *
   * This completely replaces react-day-picker's default keyboard navigation
   * to provide better UX when dealing with disabled dates and month boundaries.
   */
  React.useEffect(() => {
    const calendarElement = calendarRef.current;
    if (!calendarElement) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if we're focused on a calendar day button
      const activeElement = document.activeElement;
      const isCalendarButton =
        activeElement?.closest('[role="gridcell"]') &&
        activeElement?.tagName === 'BUTTON';

      if (!isCalendarButton) {
        return;
      }

      // Handle Space/Enter - just let react-day-picker handle the selection
      // The onDayFocus callback keeps the ref updated
      if (e.key === ' ' || e.key === 'Enter') {
        return;
      }

      // Only handle arrow keys from here on
      if (
        !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)
      ) {
        return;
      }

      // Cancel any pending focus operations from previous key presses
      if (pendingFocusRef.current !== null) {
        cancelAnimationFrame(pendingFocusRef.current);
        pendingFocusRef.current = null;
      }

      // Get current date from ref, or fallback to today
      const currentDate = currentFocusedDateRef.current || new Date();

      // ALWAYS prevent default to stop react-day-picker from handling navigation
      // We'll handle all navigation manually
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Calculate direction and step size
      let stepFn: (date: Date) => Date;
      switch (e.key) {
        case 'ArrowRight':
          stepFn = date => addDays(date, 1);
          break;
        case 'ArrowLeft':
          stepFn = date => subDays(date, 1);
          break;
        case 'ArrowDown':
          stepFn = date => addWeeks(date, 1);
          break;
        case 'ArrowUp':
          stepFn = date => subWeeks(date, 1);
          break;
        default:
          return;
      }

      // Define visible month range - calendar shows current month + next month
      const displayedMonth = props.month || currentDate;
      const displayedMonthStart = new Date(
        displayedMonth.getFullYear(),
        displayedMonth.getMonth(),
        1,
      );
      // Allow navigation only within the 2 visible months
      const allowedRangeStart = displayedMonthStart;
      const allowedRangeEnd = new Date(
        displayedMonthStart.getFullYear(),
        displayedMonthStart.getMonth() + 2,
        0,
      );

      // Find next enabled date in the direction, skipping over disabled dates
      let targetDate = stepFn(currentDate);
      let attempts = 0;
      const maxAttempts = 31; // Don't search more than a month ahead

      while (attempts < maxAttempts) {
        // Check if we've gone out of visible range
        if (targetDate < allowedRangeStart || targetDate > allowedRangeEnd) {
          // Out of range - do nothing (already prevented default)
          return;
        }

        // Check if target is enabled
        if (!isDateDisabled(targetDate)) {
          // Found an enabled date! Update ref immediately
          currentFocusedDateRef.current = targetDate;

          // Manually focus the target date button
          pendingFocusRef.current = requestAnimationFrame(() => {
            pendingFocusRef.current = null;

            // Try multiple selectors to find buttons
            let allButtons = Array.from(
              calendarElement?.querySelectorAll('[role="gridcell"] button') ||
                [],
            ) as HTMLButtonElement[];

            if (allButtons.length === 0) {
              // Fallback: try alternative selector
              allButtons = Array.from(
                calendarElement?.querySelectorAll('button') || [],
              ) as HTMLButtonElement[];
            }

            if (allButtons.length === 0) {
              // Fallback: search from document
              allButtons = Array.from(
                document.querySelectorAll('.rdp button') || [],
              ) as HTMLButtonElement[];
            }

            // Find button matching target date by day number
            const targetDay = targetDate.getDate();

            // Filter to buttons with matching day number that are NOT disabled
            const matchingButtons = allButtons.filter(button => {
              const fullText = button.textContent?.trim() || '';

              // Extract just the day number from the start of the text
              // Button might have text like "30From € 360" so we need to extract the leading number
              const match = fullText.match(/^(\d{1,2})/);
              const dayNumber = match ? parseInt(match[1]) : 0;

              // Check if day matches AND button is not disabled
              const isDisabled =
                button.hasAttribute('disabled') ||
                button.getAttribute('aria-disabled') === 'true' ||
                button.classList.contains('rdp-day_disabled');

              return dayNumber === targetDay && !isDisabled;
            });

            // If multiple buttons match (e.g., day 28 in Oct and Nov), pick the right one
            let targetButton = matchingButtons[0];

            if (matchingButtons.length > 1) {
              // Simple approach: buttons appear in DOM order (first month, then second month)
              // Calculate which button index we need based on target month vs displayed month
              const targetMonth = targetDate.getMonth();
              const targetYear = targetDate.getFullYear();
              const displayedMonth = displayedMonthStart.getMonth();
              const displayedYear = displayedMonthStart.getFullYear();

              // Calculate month difference (0 = first month shown, 1 = second month shown)
              const monthDiff =
                (targetYear - displayedYear) * 12 +
                (targetMonth - displayedMonth);

              // Pick button based on month difference
              if (monthDiff >= 0 && monthDiff < matchingButtons.length) {
                targetButton = matchingButtons[monthDiff];
              }
            }

            if (targetButton) {
              targetButton.focus();
            }
          });
          return;
        }

        // Target is disabled, try next date in the same direction
        targetDate = stepFn(targetDate);
        attempts++;
      }

      // No enabled date found within search range - do nothing (already prevented default)
    };

    // Capture phase to intercept before react-day-picker's handlers
    calendarElement.addEventListener('keydown', handleKeyDown, {
      capture: true,
    });

    return () => {
      calendarElement.removeEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    };
  }, [isDateDisabled, props.month]);

  return (
    <div ref={calendarRef}>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn('p-3', className)}
        onDayFocus={handleDayFocus}
        classNames={{
          months:
            'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_dropdowns: 'flex gap-1 w-full',
          caption_label: 'text-sm font-medium',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          ),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell:
            'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: cn(
            'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
            props.mode === 'range'
              ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
              : '[&:has([aria-selected])]:rounded-md',
          ),
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
          ),
          day_range_start: 'day-range-start',
          day_range_end: 'day-range-end',
          day_selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          day_today: 'bg-accent text-accent-foreground',
          day_outside:
            'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle:
            'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        components={{
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft
              className={cn('h-4 w-4', className)}
              aria-hidden="true"
              {...props}
            />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight
              className={cn('h-4 w-4', className)}
              aria-hidden="true"
              {...props}
            />
          ),
          Dropdown: ({
            value,
            onChange,
            children,
            ...props
          }: DropdownProps) => {
            const options = React.Children.toArray(
              children,
            ) as React.ReactElement<React.HTMLProps<HTMLOptionElement>>[];
            const selected = options.find(child => child.props.value === value);
            const handleChange = (value: string) => {
              const changeEvent = {
                target: { value },
              } as React.ChangeEvent<HTMLSelectElement>;
              onChange?.(changeEvent);
            };
            return (
              <Select
                value={value?.toString()}
                onValueChange={value => {
                  handleChange(value);
                }}
              >
                <SelectTrigger className="pr-1.5 focus:ring-0">
                  <SelectValue>{selected?.props?.children}</SelectValue>
                </SelectTrigger>
                <SelectContent position="popper">
                  <ScrollArea className="h-80">
                    {options.map((option, id: number) => (
                      <SelectItem
                        key={`${option.props.value}-${id}`}
                        value={option.props.value?.toString() ?? ''}
                      >
                        {option.props.children}
                      </SelectItem>
                    ))}
                  </ScrollArea>
                </SelectContent>
              </Select>
            );
          },
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
export type {
  DateRange,
  Matcher,
  StyledElement as CalendarStyledElement,
  Matcher as DateMatcher,
} from 'react-day-picker';
